/**
 * Procesa los datos crudos de Table1 de Acumuladores de Agentes
 * y genera métricas para el dashboard
 */
export function procesarDatosAcuAgentes(rows) {
  if (!rows || rows.length === 0) return null;

  // Ordenar por intervalo
  const sorted = [...rows].sort(
    (a, b) => new Date(a.Intervalo) - new Date(b.Intervalo)
  );

  // --- KPIs globales (sumatoria / promedios) ---
  let totalLogin = 0;
  let totalConnect = 0;
  let totalAvail = 0;
  let totalHold = 0;
  let totalACW = 0;
  let totalNotReady = 0;
  let totalUnstaffed = 0;
  let totalBreaks = 0;
  let totalRing = 0;
  let totalDial = 0;
  let totalPreview = 0;
  let totalEntrantesAtendidas = 0;
  let totalEntrantesAbandonadas = 0;
  let totalSalientesAtendidas = 0;
  let totalSalientesNoAtendidas = 0;
  let totalDiscadorAtendidas = 0;
  let totalTransferIn = 0;
  let totalTransferOut = 0;
  let totalInternasEntAtendidas = 0;
  let totalInternasSalAtendidas = 0;
  let totalRgExitoso = 0;
  let totalRgNoExitoso = 0;
  let totalRgNoEfectivo = 0;
  let totalRgNeutro = 0;
  let totalRgOtro = 0;
  let maxAgentesSimultaneos = 0;
  let totalAgentesUnicos = 0;
  let sumUtilizacion = 0;
  let sumAHT = 0;
  let countAHTNonZero = 0;

  sorted.forEach((r) => {
    const n = (v) => parseFloat(v) || 0;
    totalLogin += n(r.Login);
    totalConnect += n(r.Connect);
    totalAvail += n(r.Avail);
    totalHold += n(r.Hold);
    totalACW += n(r.ACW);
    totalNotReady += n(r.NotReady);
    totalUnstaffed += n(r.Unstaffed);
    totalRing += n(r.Ring);
    totalDial += n(r.Dial);
    totalPreview += n(r.Preview);
    totalBreaks +=
      n(r.Break0) + n(r.Break1) + n(r.Break2) + n(r.Break3) + n(r.Break4) +
      n(r.Break5) + n(r.Break6) + n(r.Break7) + n(r.Break8) + n(r.Break9);
    totalEntrantesAtendidas += n(r.EntrantesAtendidas);
    totalEntrantesAbandonadas += n(r.EntrantesAbandonadas);
    totalSalientesAtendidas += n(r.SalientesAtendidas);
    totalSalientesNoAtendidas += n(r.SalientesNoAtendidas);
    totalDiscadorAtendidas += n(r.DiscadorAtendidas);
    totalTransferIn += n(r.TransferIn);
    totalTransferOut += n(r.TransferOut);
    totalInternasEntAtendidas += n(r.InternasEntrantesAtendidas);
    totalInternasSalAtendidas += n(r.InternasSalientesAtendidas);
    totalRgExitoso += n(r.rgExitoso);
    totalRgNoExitoso += n(r.rgNoExitoso);
    totalRgNoEfectivo += n(r.rgNoEfectivo);
    totalRgNeutro += n(r.rgNeutro);
    totalRgOtro += n(r.rgOtro);
    const agSim = n(r.CantAgentesSimultaneos);
    if (agSim > maxAgentesSimultaneos) maxAgentesSimultaneos = agSim;
    totalAgentesUnicos = Math.max(totalAgentesUnicos, n(r.CantAgentesUnicos));
    sumUtilizacion += n(r.Utilizacion);
    const aht = n(r.AHT);
    if (aht > 0) { sumAHT += aht; countAHTNonZero++; }
  });

  const totalLlamadas =
    totalEntrantesAtendidas + totalEntrantesAbandonadas +
    totalSalientesAtendidas + totalSalientesNoAtendidas +
    totalDiscadorAtendidas;
  const totalLlamadasAtendidas =
    totalEntrantesAtendidas + totalSalientesAtendidas + totalDiscadorAtendidas;
  const promedioUtilizacion = sorted.length > 0 ? sumUtilizacion / sorted.length : 0;
  const promedioAHT = countAHTNonZero > 0 ? sumAHT / countAHTNonZero : 0;

  // --- Datos por intervalo para gráficos ---
  const labels = sorted.map((r) => {
    const d = new Date(r.Intervalo);
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  });

  const n = (v) => parseFloat(v) || 0;

  const chartTiempo = {
    labels,
    connect: sorted.map((r) => n(r.Connect)),
    avail: sorted.map((r) => n(r.Avail)),
    hold: sorted.map((r) => n(r.Hold)),
    acw: sorted.map((r) => n(r.ACW)),
    notReady: sorted.map((r) => n(r.NotReady)),
    breaks: sorted.map(
      (r) =>
        n(r.Break0) + n(r.Break1) + n(r.Break2) + n(r.Break3) + n(r.Break4) +
        n(r.Break5) + n(r.Break6) + n(r.Break7) + n(r.Break8) + n(r.Break9)
    ),
  };

  const chartLlamadas = {
    labels,
    entrantesAtendidas: sorted.map((r) => n(r.EntrantesAtendidas)),
    entrantesAbandonadas: sorted.map((r) => n(r.EntrantesAbandonadas)),
    salientesAtendidas: sorted.map((r) => n(r.SalientesAtendidas)),
    salientesNoAtendidas: sorted.map((r) => n(r.SalientesNoAtendidas)),
  };

  const chartUtilizacion = {
    labels,
    utilizacion: sorted.map((r) => (n(r.Utilizacion) * 100).toFixed(1)),
    connectReal: sorted.map((r) => (n(r.ConnectReal) * 100).toFixed(1)),
    availReal: sorted.map((r) => (n(r.AvailReal) * 100).toFixed(1)),
  };

  const chartAgentes = {
    labels,
    unicos: sorted.map((r) => n(r.CantAgentesUnicos)),
    simultaneos: sorted.map((r) => n(r.CantAgentesSimultaneos)),
  };

  // Distribución de breaks
  const breakLabels = [
    'Descanso', 'Comida', 'Capacitación', 'Atención Presencial', 'Baño',
    'Cobranza', 'Monitoreo Red', 'Tareas Operaciones', 'Break 8', 'Break 9',
  ];
  const breakTotals = Array(10).fill(0);
  sorted.forEach((r) => {
    for (let i = 0; i <= 9; i++) breakTotals[i] += n(r[`Break${i}`]);
  });

  // Tipificaciones
  const tipificaciones = {
    exitoso: totalRgExitoso,
    noExitoso: totalRgNoExitoso,
    noEfectivo: totalRgNoEfectivo,
    neutro: totalRgNeutro,
    otro: totalRgOtro,
  };

  return {
    kpis: {
      totalLlamadas,
      totalLlamadasAtendidas,
      totalEntrantesAtendidas,
      totalEntrantesAbandonadas,
      totalSalientesAtendidas,
      totalSalientesNoAtendidas,
      totalDiscadorAtendidas,
      totalTransferIn,
      totalTransferOut,
      totalInternasEntAtendidas,
      totalInternasSalAtendidas,
      promedioUtilizacion,
      promedioAHT,
      maxAgentesSimultaneos,
      totalAgentesUnicos,
      totalLogin,
      totalConnect,
      totalAvail,
      totalHold,
      totalACW,
      totalNotReady,
      totalBreaks,
      totalUnstaffed,
      intervalos: sorted.length,
    },
    chartTiempo,
    chartLlamadas,
    chartUtilizacion,
    chartAgentes,
    breakLabels,
    breakTotals,
    tipificaciones,
    rows: sorted,
  };
}

/** Formatea segundos a HH:MM:SS */
export function fmtSeg(s) {
  if (!s || s === 0) return '00:00:00';
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.round(s % 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
}

/** Formatea porcentaje */
export function fmtPct(v) {
  return (v * 100).toFixed(1) + '%';
}
